import {
    formatDateFields, isDigit, javaFormatFixed, javaTrim, parseDateFields, parseJavaDouble,
} from './javaCompat';

/**
 * Delphi/FastReport display formatting (port of Fr3Format).
 *
 * The corpus uses "%2.2n", "dd/mm/yyyy", "%g", "%2.0n" and
 * FormatFloat('0.00', x) from script, so this implements those forms rather
 * than the full Delphi format grammar.
 */
export class Fr3Format {
    constructor() {
        /** Belgian conventions, which is what these documents are printed under. */
        this.decimalSeparator = ',';
        this.thousandSeparator = '.';
    }

    apply(raw, formatKind, formatStr, hideZeros) {
        if (raw == null) return '';
        const s = javaTrim(raw);
        if (formatStr == null || javaTrim(formatStr) === '') return s;
        const fmt = javaTrim(formatStr);

        if (formatKind === 'fkDateTime' || looksLikeDatePattern(fmt)) {
            const formatted = formatDate(s, fmt);
            return formatted == null ? s : formatted;
        }

        const v = Fr3Format.toDouble(s);
        if (v == null) return s;
        if (hideZeros && v === 0) return '';
        return this.formatNumber(v, fmt);
    }

    formatNumber(v, fmt) {
        const f = fmt == null ? '' : javaTrim(fmt);
        if (f.toLowerCase() === '%g') return this.general(v);
        // "%2.2n" / "%2.0n": the digits after the dot are the decimal count and
        // 'n' asks for thousand separators.
        let decimals = 2;
        let grouped = true;
        if (f.startsWith('%')) {
            const dot = f.indexOf('.');
            if (dot >= 0 && dot + 1 < f.length) {
                let i = dot + 1;
                let d = 0;
                let any = false;
                while (i < f.length && isDigit(f[i])) {
                    d = d * 10 + (f.charCodeAt(i) - 48);
                    i++;
                    any = true;
                }
                if (any) decimals = d;
            }
            const conv = f[f.length - 1];
            grouped = conv === 'n' || conv === 'N';
        }
        return this.fixed(v, decimals, grouped);
    }

    /** FormatFloat('0.00', x) as used by the templates' scripts. */
    formatFloat(pattern, v) {
        const p = pattern == null ? '' : javaTrim(pattern);
        const dot = p.indexOf('.');
        const decimals = dot < 0 ? 0 : p.length - dot - 1;
        const grouped = p.indexOf(',') >= 0;
        return this.fixed(v, decimals, grouped);
    }

    fixed(v, decimals, grouped) {
        if (decimals < 0) decimals = 0;
        const neg = v < 0 || (v === 0 && 1 / v < 0);
        const a = Math.abs(v);
        const s = javaFormatFixed(a, decimals);
        let intPart = s;
        let fracPart = '';
        const dot = s.indexOf('.');
        if (dot >= 0) {
            intPart = s.substring(0, dot);
            fracPart = s.substring(dot + 1);
        }
        if (grouped) intPart = this.group(intPart);
        let out = '';
        if (neg && !isAllZero(intPart, fracPart)) out += '-';
        out += intPart;
        if (fracPart !== '') out += this.decimalSeparator + fracPart;
        return out;
    }

    group(digits) {
        let sb = '';
        let c = 0;
        for (let i = digits.length - 1; i >= 0; i--) {
            sb += digits[i];
            c++;
            if (c % 3 === 0 && i > 0) sb += this.thousandSeparator;
        }
        return sb.split('').reverse().join('');
    }

    /** Delphi FloatToStr: shortest representation, locale decimal separator. */
    floatToStr(v) {
        return this.general(v);
    }

    general(v) {
        if (Number.isFinite(v) && Number.isInteger(v) && Math.abs(v) < 1e15) {
            return this.fixed(v, 0, false);
        }
        let s = javaFormatFixed(v, 6);
        while (s.endsWith('0')) s = s.substring(0, s.length - 1);
        if (s.endsWith('.')) s = s.substring(0, s.length - 1);
        return s.split('.').join(this.decimalSeparator);
    }

    /**
     * Renders a bare SQL date the way the Windows till does: a memo bound to a
     * date column prints as a locale short date. Our rows are strings, so the
     * ISO shape is recognised instead; anything else is returned untouched.
     */
    autoFormatDate(value) {
        if (value == null) return null;
        const s = javaTrim(value);
        if (s.length < 10 || s.length > 25) return value;
        for (let i = 0; i < 10; i++) {
            const c = s[i];
            const expectDash = i === 4 || i === 7;
            if (expectDash ? c !== '-' : !isDigit(c)) return value;
        }
        const d = parseDate(s);
        if (d == null) return value;
        return formatDateFields('dd/MM/yyyy', d);
    }

    static toDouble(s) {
        if (s == null) return null;
        let t = javaTrim(String(s));
        if (t === '') return null;
        t = t.split(' ').join('');
        // Accept both separators: values arrive from SQL as "12.34" but scripts
        // and literals may carry a comma.
        if (t.indexOf(',') >= 0 && t.indexOf('.') >= 0) {
            t = t.split('.').join('').split(',').join('.');
        } else {
            t = t.split(',').join('.');
        }
        return parseJavaDouble(t);
    }
}

const isAllZero = (intPart, fracPart) => {
    for (let i = 0; i < intPart.length; i++) {
        if (isDigit(intPart[i]) && intPart[i] !== '0') return false;
    }
    for (let i = 0; i < fracPart.length; i++) {
        if (fracPart[i] !== '0') return false;
    }
    return true;
};

const looksLikeDatePattern = (fmt) => {
    const f = fmt.toLowerCase();
    return f.includes('yyyy') || f.includes('dd/') || f.includes('mm/');
};

const formatDate = (raw, delphiPattern) => {
    const d = parseDate(raw);
    if (d == null) return null;
    // Delphi spells months "mm" and 24h hours "hh"; d and y already agree.
    const java = delphiPattern.split('mm').join('MM').split('hh').join('HH');
    return formatDateFields(java, d);
};

const DATE_PATTERNS = [
    'yyyy-MM-dd HH:mm:ss', 'yyyy-MM-dd\'T\'HH:mm:ss', 'yyyy-MM-dd',
    'dd/MM/yyyy HH:mm:ss', 'dd/MM/yyyy', 'dd-MM-yyyy',
];

const parseDate = (raw) => {
    const s = javaTrim(raw);
    if (s === '' || s.startsWith('0000-00-00')) return null;
    for (const p of DATE_PATTERNS) {
        const f = parseDateFields(p, s);
        if (f) return f;
    }
    return null;
};

export default Fr3Format;
